import * as Sentry from '@sentry/react';

/**
 * Utility for tracing API calls and responses to Sentry
 * Only works when VITE_DEBUG is "false" (production mode)
 */

interface ApiCallOptions {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
}

interface ApiResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: unknown;
  url: string;
}

/**
 * Wrapper for fetch that traces API calls and responses to Sentry
 */
export const tracedFetch = async (
  url: string,
  options: RequestInit = {},
  context?: Record<string, unknown>
): Promise<Response> => {
  const startTime = performance.now();
  const method = options.method || 'GET';
  const requestId = Math.random().toString(36).substring(7);

  // Create a span for the API call (simplified for now)
  const span = null; // We'll implement proper span creation later if needed

  // Add breadcrumb for the request
  if (import.meta.env.VITE_DEBUG === "false") {
    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      data: {
        url,
        method,
        request_id: requestId,
        ...context,
      },
      level: 'info',
    });
  }

  try {
    const response = await fetch(url, options);
    const duration = performance.now() - startTime;

    // Get response data (clone response to avoid consuming it)
    const responseClone = response.clone();
    let responseData: unknown = null;
    let responseText = '';

    try {
      responseText = await responseClone.text();
      // Try to parse as JSON
      try {
        responseData = JSON.parse(responseText);
      } catch {
        // If not JSON, use the text
        responseData = responseText;
      }
    } catch (error) {
      // If we can't read the response, that's okay
      responseData = null;
    }

    // Add breadcrumb for the response
    if (import.meta.env.VITE_DEBUG === "false") {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        data: {
          url,
          method,
          status_code: response.status,
          status_text: response.statusText,
          response_size: responseText.length,
          duration: `${duration.toFixed(2)}ms`,
          request_id: requestId,
          ...context,
        },
        level: response.ok ? 'info' : 'warning',
      });

      // Set span data
      span?.setData('http.status_code', response.status);
      span?.setData('http.response_size', responseText.length);
      span?.setData('http.duration', duration);
    }

    // If response is not ok, capture it as an error
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).status = response.status;
      (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).url = url;
      (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).method = method;
      (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).responseData = responseData;

      if (import.meta.env.VITE_DEBUG === "false") {
        Sentry.captureException(error, {
          contexts: {
            http: {
              url,
              method,
              status_code: response.status,
              status_text: response.statusText,
              response_data: responseData,
              request_id: requestId,
              ...context,
            },
          },
          tags: {
            request_id: requestId,
            http_method: method,
            http_status: response.status.toString(),
          },
        });
      }

      span?.setStatus('error');
    } else {
      span?.setStatus('ok');
    }

    span?.finish();

    return response;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Add breadcrumb for the error
    if (import.meta.env.VITE_DEBUG === "false") {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        data: {
          url,
          method,
          error: error instanceof Error ? error.message : String(error),
          duration: `${duration.toFixed(2)}ms`,
          request_id: requestId,
          ...context,
        },
        level: 'error',
      });

      // Capture the error
      Sentry.captureException(error, {
        contexts: {
          http: {
            url,
            method,
            error: error instanceof Error ? error.message : String(error),
            request_id: requestId,
            ...context,
          },
        },
        tags: {
          request_id: requestId,
          http_method: method,
        },
      });
    }

    span?.setStatus('error');
    span?.finish();

    throw error;
  }
};

/**
 * Utility to trace API responses without wrapping fetch
 * Use this when you want to trace existing API calls
 */
export const traceApiResponse = (
  url: string,
  method: string,
  response: Response,
  responseData: unknown,
  duration: number,
  context?: Record<string, unknown>
) => {
  if (import.meta.env.VITE_DEBUG !== "false") return;

  const requestId = Math.random().toString(36).substring(7);

  // Add breadcrumb for the response
  Sentry.addBreadcrumb({
    category: 'http',
    type: 'http',
    data: {
      url,
      method,
      status_code: response.status,
      status_text: response.statusText,
      response_size: JSON.stringify(responseData).length,
      duration: `${duration.toFixed(2)}ms`,
      request_id: requestId,
      ...context,
    },
    level: response.ok ? 'info' : 'warning',
  });

  // If response is not ok, capture it as an error
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).status = response.status;
    (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).url = url;
    (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).method = method;
    (error as Error & { status?: number; url?: string; method?: string; responseData?: unknown }).responseData = responseData;

    Sentry.captureException(error, {
      contexts: {
        http: {
          url,
          method,
          status_code: response.status,
          status_text: response.statusText,
          response_data: responseData,
          request_id: requestId,
          ...context,
        },
      },
      tags: {
        request_id: requestId,
        http_method: method,
        http_status: response.status.toString(),
      },
    });
  }
};

/**
 * Utility to trace API errors
 */
export const traceApiError = (
  url: string,
  method: string,
  error: Error,
  context?: Record<string, unknown>
) => {
  if (import.meta.env.VITE_DEBUG !== "false") return;

  const requestId = Math.random().toString(36).substring(7);

  Sentry.captureException(error, {
    contexts: {
      http: {
        url,
        method,
        error: error.message,
        request_id: requestId,
        ...context,
      },
    },
    tags: {
      request_id: requestId,
      http_method: method,
    },
  });
}; 